import { memo } from 'react';
import { Group, Circle, Ellipse, Line, Rect, Wedge } from 'react-konva';

/**
 * Sun Character - happy smiling sun with rays, face, and little arms/legs.
 */
export const SunCharacter = memo(function SunCharacter({ x, y, scaleX, scaleY, rotation, opacity, fill, customColors, leftArmAngle, rightArmAngle, onClick, onDragEnd, onTransformEnd, draggable, isSelected }) {
  const c = customColors || {};
  const sunColor = c.fill || fill || '#fbbf24';
  const sunDark = '#f59e0b';
  const rayColor = c.rayColor || '#fde047';
  const cheekColor = '#f97316';

  const rays = [];
  for (let i = 0; i < 12; i++) {
    const angle = (i * 30) * Math.PI / 180;
    const inner = 30;
    const outer = i % 2 === 0 ? 48 : 40;
    const x1 = Math.cos(angle) * inner;
    const y1 = Math.sin(angle) * inner;
    const x2 = Math.cos(angle) * outer;
    const y2 = Math.sin(angle) * outer;
    rays.push(
      <Line
        key={i}
        points={[x1, y1, x2, y2]}
        stroke={rayColor}
        strokeWidth={i % 2 === 0 ? 6 : 4}
        lineCap="round"
      />
    );
  }

  return (
    <Group
      x={x} y={y}
      scaleX={scaleX || 1} scaleY={scaleY || 1}
      rotation={rotation || 0}
      opacity={opacity ?? 1}
      draggable={draggable}
      onClick={() => onClick?.()}
      onTap={() => onClick?.()}
      onDragEnd={(e) => onDragEnd?.({ x: e.target.x(), y: e.target.y() })}
      onTransformEnd={(e) => {
        const node = e.target;
        onTransformEnd?.({
          x: node.x(), y: node.y(),
          scaleX: node.scaleX(), scaleY: node.scaleY(),
          rotation: node.rotation(),
        });
      }}
    >
      {/* Shadow */}
      <Ellipse x={0} y={58} radiusX={20} radiusY={5} fill="#000" opacity={0.1} />

      {/* Little legs */}
      <Line points={[-8,28, -10,48]} stroke={sunColor} strokeWidth={5} lineCap="round" />
      <Ellipse x={-12} y={50} radiusX={6} radiusY={3} fill={sunDark} />
      <Line points={[8,28, 10,48]} stroke={sunColor} strokeWidth={5} lineCap="round" />
      <Ellipse x={12} y={50} radiusX={6} radiusY={3} fill={sunDark} />

      {/* Sun rays */}
      {rays}

      {/* Main sun body */}
      <Circle x={0} y={0} radius={28} fill={sunColor} />
      <Circle x={0} y={0} radius={24} fill={sunColor} />
      {/* Inner glow */}
      <Circle x={-5} y={-5} radius={18} fill="#fde68a" opacity={0.3} />

      {/* Little arms - animated */}
      <Group rotation={leftArmAngle || 0} x={-26} y={5}>
        <Line points={[0,0, -12,-5, -16,-13]} stroke={sunColor} strokeWidth={5} lineCap="round" lineJoin="round" />
        <Circle x={-16} y={-13} radius={3.5} fill={sunDark} />
      </Group>
      <Group rotation={rightArmAngle || 0} x={26} y={5}>
        <Line points={[0,0, 12,-5, 16,-13]} stroke={sunColor} strokeWidth={5} lineCap="round" lineJoin="round" />
        <Circle x={16} y={-13} radius={3.5} fill={sunDark} />
      </Group>

      {/* Cheeks */}
      <Ellipse x={-14} y={5} radiusX={5} radiusY={3.5} fill={cheekColor} opacity={0.25} />
      <Ellipse x={14} y={5} radiusX={5} radiusY={3.5} fill={cheekColor} opacity={0.25} />

      {/* Eyes - big happy */}
      <Ellipse x={-9} y={-5} radiusX={5} radiusY={5.5} fill="white" />
      <Circle x={-9} y={-4} radius={3} fill="#1e1e1e" />
      <Circle x={-8} y={-5.5} radius={1.2} fill="white" />
      <Ellipse x={9} y={-5} radiusX={5} radiusY={5.5} fill="white" />
      <Circle x={9} y={-4} radius={3} fill="#1e1e1e" />
      <Circle x={10} y={-5.5} radius={1.2} fill="white" />

      {/* Eyebrows - happy arches */}
      <Line points={[-14,-10, -9,-13, -4,-10]} stroke="#b45309" strokeWidth={1.2} lineCap="round" tension={0.4} />
      <Line points={[4,-10, 9,-13, 14,-10]} stroke="#b45309" strokeWidth={1.2} lineCap="round" tension={0.4} />

      {/* Nose */}
      <Circle x={0} y={1} radius={1.5} fill={sunDark} opacity={0.5} />

      {/* Big smile */}
      <Line points={[-10,7, -6,12, 0,14, 6,12, 10,7]} stroke="#92400e" strokeWidth={2} lineCap="round" tension={0.4} />
      {/* Tongue hint */}
      <Ellipse x={0} y={12} radiusX={4} radiusY={2.5} fill="#f87171" opacity={0.5} />

      {/* Selection */}
      {isSelected && <Circle x={0} y={0} radius={55} stroke="#fbbf24" strokeWidth={1.5} dash={[6,4]} fill="transparent" opacity={0.4} />}
    </Group>
  );
});
